import React from 'react'
import { OneBlinkAppsError, formService } from '@oneblink/apps-react'
import { FormTypes, MiscTypes } from '@oneblink/types'
import config from '../config'
import { setOneBlinkError } from '../hooks/useOneBlinkError'
import { getOBApi } from '../lib/server-validation'
import useQuery from './useQuery'
import { postRequest } from '../lib/http'
import { useAppConfig } from '../providers/AppConfigProvider'

export default function useFetchForm(
  formId: number | undefined,
  formSlug: string | undefined,
  runPersonalisation: boolean,
) {
  const { formsAppEnvironmentId } = useAppConfig()
  const [state, setState] = React.useState<{
    fetchError?: OneBlinkAppsError
    isFetchingForm: boolean
    formDefinition: FormTypes.Form | null
    personalisationSubmission?: Record<string, unknown>
    personalisationFetchingError?: Error
  }>({ isFetchingForm: true, formDefinition: null })

  const query = useQuery()

  const queryStringPreFillData = React.useMemo(() => {
    if (typeof query.preFillData === 'string')
      return JSON.parse(query.preFillData) as Record<string, unknown>
  }, [query])

  const getUrl = React.useCallback(
    (
      endpointConfig:
        | MiscTypes.EndpointConfiguration
        | FormTypes.ExternalIdGeneration
        | undefined,
    ) => {
      if (!endpointConfig) {
        return
      }
      if (endpointConfig?.type === 'ONEBLINK_API') {
        const { apiId, apiEnvironment, apiEnvironmentRoute } =
          endpointConfig.configuration
        return `https://${getOBApi(
          apiId,
          apiEnvironment,
        )}${apiEnvironmentRoute}`
      }

      if (endpointConfig?.type === 'CALLBACK') {
        return endpointConfig.configuration.url
      }
    },
    [],
  )

  React.useEffect(() => {
    const abortController = new AbortController()
    const fetchForm = async () => {
      try {
        const formDefinition = await formService.getForm({
          formId,
          formsAppId: config.FORMS_APP_ID,
          formSlug,
          formsAppEnvironmentId,
          abortSignal: abortController.signal,
        })
        if (
          formDefinition.id !== config.FORMS.SAVE_DRAFT_RECEIPT_FORM_ID &&
          !formDefinition.formsAppIds.includes(config.FORMS_APP_ID)
        ) {
          throw new Error('Invalid form Id')
        }
        if (abortController.signal.aborted) return

        const personalisation = formDefinition.personalisation

        const personalisationUrl = getUrl(personalisation)

        if (!runPersonalisation || !personalisationUrl) {
          setState({ formDefinition, isFetchingForm: false })
          return
        }

        try {
          const { submission, elements } = await postRequest<{
            submission: Record<string, unknown>
            elements: FormTypes.FormElement[]
          }>({
            url: personalisationUrl,
            resource: {
              formsAppId: config.FORMS_APP_ID,
              formId: formDefinition.id,
              preFillData: queryStringPreFillData || null,
              externalIdUrlSearchParam: query.externalId || null,
            },
            abortSignal: abortController.signal,
            addAuthHeaders: formDefinition.isAuthenticated,
          })

          setState({
            personalisationSubmission: submission,
            isFetchingForm: false,
            formDefinition: {
              ...formDefinition,
              elements: elements ?? formDefinition.elements,
              isMultiPage: Array.isArray(elements)
                ? elements[0]?.type === 'page'
                : formDefinition.isMultiPage,
            },
          })
        } catch (e) {
          if (abortController.signal.aborted) return
          console.error(
            'An error has occurred while calling the personalisation endpoint: ',
            e,
          )
          setState({
            formDefinition,
            isFetchingForm: false,
            personalisationFetchingError: e as Error,
          })
        }
      } catch (e) {
        if (abortController.signal.aborted) return
        setState({
          formDefinition: null,
          isFetchingForm: false,
          fetchError: setOneBlinkError(e instanceof Error ? e : undefined),
        })
      }
    }

    fetchForm()
    return () => {
      abortController.abort()
    }
  }, [
    formId,
    runPersonalisation,
    getUrl,
    queryStringPreFillData,
    formSlug,
    formsAppEnvironmentId,
    query.externalId,
  ])

  return state
}
